import { Show, createMemo, type Component, type JSX } from 'solid-js';
import { Panel } from '~ui/panel';
import { Text } from '~ui/text';
import { Table, type Column } from '~ui/table';
import { NA, formatCurrency, formatDay, formatMonth, formatNumber, formatPercent } from '~/lib/format';
import { accounts } from '~/stores/portfolio-store';
import { bySymbol, byMonth, growthSeries, incompleteSymbols, monthlySeries, payments, range, totalReceived, type DividendPayment, type PeriodTotal } from '~/stores/dividend-store';
import { Card } from '~/features/cards';
import PaymentsChart, { paymentsSummary } from '~/features/payments-chart';
import GrowthChart, { growthSummary } from '~/features/growth-chart';

const accountLabel = (id: string): string => accounts().find((a) => a.id === id)?.label ?? id;

const PAYMENT_COLUMNS: Column<DividendPayment>[] = [
    { id: 'pay', header: 'Paid', size: 1, cell: (p) => formatDay(p().payDate ?? p().exDate) },
    { id: 'ex', header: 'Ex-date', size: 1, cell: (p) => <Text tone='muted'>{formatDay(p().exDate)}</Text> },
    { id: 'symbol', header: 'Symbol', size: 0.7, cell: (p) => <Text weight='medium'>{p().symbol}</Text> },
    { id: 'account', header: 'Account', size: 0.8, cell: (p) => <Text class='truncate'>{accountLabel(p().accountId)}</Text> },
    { id: 'units', header: 'Units', align: 'right', size: 0.8, cell: (p) => formatNumber(p().units, { maximumFractionDigits: 4 }) },
    { id: 'perunit', header: 'Per unit', align: 'right', size: 0.8, cell: (p) => formatCurrency(p().perUnit, 4) },
    { id: 'amount', header: 'Amount', align: 'right', size: 1, cell: (p) => <Text tone='green'>{formatCurrency(p().amount)}</Text> },
];

const PERIOD_COLUMNS: Column<PeriodTotal>[] = [
    { id: 'period', header: 'Period', size: 1, cell: (r) => <Text weight='medium'>{r().period}</Text> },
    { id: 'count', header: 'Payments', align: 'right', size: 1, cell: (r) => formatNumber(r().payments) },
    { id: 'amount', header: 'Received', align: 'right', size: 1, cell: (r) => <Text tone='green'>{formatCurrency(r().amount)}</Text> },
];

const SYMBOL_COLUMNS: Column<{ symbol: string; amount: number; payments: number }>[] = [
    { id: 'symbol', header: 'Symbol', size: 1, cell: (r) => <Text weight='medium'>{r().symbol}</Text> },
    { id: 'count', header: 'Payments', align: 'right', size: 1, cell: (r) => formatNumber(r().payments) },
    { id: 'amount', header: 'Received', align: 'right', size: 1, cell: (r) => <Text tone='green'>{formatCurrency(r().amount)}</Text> },
];

/** One chart of the top band: a title band, its one-line record on the right,
    and the plot under it. Same title row as Cell, but sized to a chart rather
    than to a scrolling table. */
const ChartCell: Component<{ title: string; note?: string | null; children: JSX.Element }> = (props) => (
    <div class='flex min-w-0 flex-1 flex-col bg-background'>
        <div class='flex h-row shrink-0 items-center justify-between gap-2 border-b px-2 whitespace-nowrap'>
            <Text weight='medium'>{props.title}</Text>
            <Text tone='muted' class='truncate'>
                {props.note}
            </Text>
        </div>
        {/* Fixed here rather than left to the chart, so the empty state gets the
            same band the plot would have had. A growth chart waiting on its
            first year renders a sentence, and a sentence has no height of its
            own — the two cells would sit at different heights for the first year
            of every account. */}
        <div class='flex h-44 min-h-0 flex-col'>{props.children}</div>
    </div>
);

/**
 * One cell of the grid: a title band over a table that owns its own scroll.
 *
 * The title has to be here now. It was carried by the selected tab before, and
 * three tables on screen at once with nothing naming them is three sets of
 * numbers in the same green.
 */
const Cell: Component<{ title: string; children: JSX.Element }> = (props) => (
    <div class='flex min-h-0 min-w-0 flex-1 flex-col bg-background'>
        <div class='flex h-row shrink-0 items-center border-b px-2'>
            <Text weight='medium'>{props.title}</Text>
        </div>
        {props.children}
    </div>
);

// Three views of one set of payments, side by side rather than behind tabs.
//
// They answer different questions and are read against each other: the month
// column is "is income growing", the symbol column is "which fund is paying for
// this", and the detail list is the evidence for both. Tabbing between them made
// that a memory exercise.
//
// Detail takes two thirds because it is the only one with seven columns; the
// two summaries are three columns each and stack comfortably in the rest.
const Dividends: Component = () => {
    const empty = createMemo(() => (payments().length === 0 ? 'No distributions paid on any position yet' : 'No data'));

    /** The most recent month that has one behind it to be measured against. */
    const latest = createMemo(() => {
        const all = growthSeries();
        return all.length === 0 ? null : all[all.length - 1];
    });
    const change = () => (latest() == null ? null : latest()!.now - latest()!.then);
    /** The month being compared against, which is what "on" refers to. */
    const previous = createMemo(() => {
        const all = monthlySeries();
        return all.length < 2 ? null : all[all.length - 2].period;
    });

    return (
        <Panel>
            {/* No toolbar. It held one permanent sentence — "actually paid, from
                ex-date entitlement" — which is a note about how the page works,
                true on every load and read once. A whole band of the pane, above
                everything, spent restating the page's own premise.

                The one thing on it that was ever news, the incomplete-history
                warning, moved to the footer beside the range: it is a caveat on
                those figures, so it belongs next to them rather than a screen
                away. */}

            {/* The one number the page is for, first. Everything under it — two
                charts and three tables — is a way of asking how this total came
                about; you read a total before you read a ledger, and it was at
                the bottom of the pane behind a scroll. */}
            <div class='flex shrink-0 gap-px border-b bg-border'>
                <div class='min-w-0 flex-1'>
                    <Card label='Received' value={formatCurrency(totalReceived())} tone='green'>
                        {formatNumber(payments().length)} payments{range() ? ` · since ${formatDay(range()!.from)}` : ''}
                    </Card>
                </div>
                {/* Dollars, not the percentage — the percentage is the chart
                    below and is the shape of the thing; this is the size of it.
                    A month up 40% is worth knowing about at $300 and is noise at
                    $4, and only one of those two figures tells you which.

                    Hidden until there is a month to compare against, rather than
                    reading $0.00 on the first one. */}
                <Show when={latest()}>
                    <div class='min-w-0 flex-1'>
                        <Card
                            label='Growth'
                            value={formatCurrency(change())}
                            tone={change() == null ? undefined : change()! >= 0 ? 'green' : 'red'}>
                            {latest()!.pct == null ? 'from nothing' : `${formatPercent(latest()!.pct, 1)} on ${formatMonth(previous()!)}`}
                        </Card>
                    </div>
                </Show>
            </div>

            {/* Full width, above the grid, rather than stacked over the Monthly
                column it plots. Sixty months in a third of the pane is a month
                every four pixels, and the trend this exists to show is the first
                thing lost to it. It is also the page's headline: the tables are
                the evidence, and you read a total before you read a ledger. */}
            <Show when={payments().length > 0}>
                <div class='flex shrink-0 gap-px border-b bg-border'>
                    {/* Two halves, not a wide one and a narrow one. They are read
                        against each other — a total climbing steadily and a total
                        climbing and flattening are the same picture on the left
                        and different pictures on the right — and an unequal split
                        would say one of them is the answer and the other the
                        aside. */}
                    <ChartCell title='Per month' note={paymentsSummary()}>
                        <PaymentsChart />
                    </ChartCell>
                    <ChartCell title='Dividend growth' note={growthSummary() ?? 'month on month'}>
                        <GrowthChart />
                    </ChartCell>
                </div>
            </Show>

            <div class='flex min-h-0 flex-1 gap-px bg-border'>
                <div class='flex w-1/3 min-w-0 shrink-0 flex-col gap-px bg-border'>
                    <Cell title='Monthly'>
                        <Table columns={PERIOD_COLUMNS} rows={byMonth()} empty={empty()} />
                    </Cell>
                    <Cell title='By symbol'>
                        <Table columns={SYMBOL_COLUMNS} rows={bySymbol()} empty={empty()} />
                    </Cell>
                </div>

                <Cell title='Detail'>
                    <Table columns={PAYMENT_COLUMNS} rows={payments()} empty={empty()} />
                </Cell>
            </div>

            {/* One footer for the page rather than one per table. The range and
                the total are facts about the whole set of payments — repeated in
                three columns they would read as three different totals that
                happen to agree. */}
            <div class='flex h-row shrink-0 items-center justify-between gap-2 border-t bg-background px-2 whitespace-nowrap'>
                <Text tone='muted'>
                    {range() ? `${formatDay(range()!.from)} — ${formatDay(range()!.to)}` : NA} · {formatNumber(payments().length)} payments
                </Text>
                <div class='flex min-w-0 items-baseline gap-3'>
                    {/* Only when a symbol's distribution history starts after
                        its first buy, which under-reports the total on the card
                        at the top of the page. The total itself is not repeated
                        here — one figure, one place, or the two read as two
                        totals that happen to agree. */}
                    <Show when={incompleteSymbols().length > 0}>
                        <Text tone='red' class='truncate'>
                            Incomplete history: {incompleteSymbols().join(', ')}
                        </Text>
                    </Show>
                </div>
            </div>
        </Panel>
    );
};

export default Dividends;
